import React, { useEffect, useState } from "react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Plus, Pencil, Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = { SUPERADMIN: "bg-slate-800 text-white", DIRECAO: "bg-blue-100 text-blue-700", COORDENACAO: "bg-violet-100 text-violet-700", PROFESSOR: "bg-emerald-100 text-emerald-700", COZINHEIRA: "bg-orange-100 text-orange-700" };

export default function UsersPage() {
  const { user, scopeParams, schools, isSuperadmin, selectedSchoolId } = useAuth();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [teachers, setTeachers] = useState([]);

  const roleOptions = user.role === "SUPERADMIN" ? ["SUPERADMIN", "DIRECAO", "COORDENACAO", "PROFESSOR", "COZINHEIRA"] : ["DIRECAO", "COORDENACAO", "PROFESSOR", "COZINHEIRA"];
  const fetch = () => api.get("/users", { params: scopeParams }).then((r) => setUsers(r.data));
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [selectedSchoolId]);
  useEffect(() => {
    const sid = form.school_id || user.school_id;
    if (!open || form.role !== "PROFESSOR" || !sid) { setTeachers([]); return; }
    api.get("/teachers", { params: { school_id: sid } }).then((r) => setTeachers(r.data)).catch(() => setTeachers([]));
  }, [open, form.role, form.school_id, user.school_id]);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", role: "PROFESSOR", phone: "", status: "ATIVO", school_id: selectedSchoolId || "", password: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) { if (!payload.password) delete payload.password; await api.put(`/users/${editing.id}`, payload); }
      else await api.post("/users", payload);
      toast.success(editing ? "Usuário atualizado" : "Usuário cadastrado");
      setOpen(false); fetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [delUser, setDelUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/users/${delUser.id}`); toast.success("Usuário excluído"); setDelUser(null); fetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  if (users === null) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;
  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Usuários e Permissões" subtitle="Gerencie acessos por perfil e escola">
        {user.role !== "COORDENACAO" && (
          <Button onClick={openNew} data-testid="user-add-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Novo Usuário</Button>
        )}
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="users-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou e-mail" className="pl-9" />
      </div>

      {filtered.length === 0 ? <EmptyState icon={Users} title="Nenhum usuário encontrado" /> : (
        <Card className="overflow-x-auto border-slate-200">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nome</TableHead><TableHead>E-mail</TableHead><TableHead>Perfil</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell><span data-testid="user-role-badge" className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</span></TableCell>
                  <TableCell><StatusBadge status={u.status} /></TableCell>
                  <TableCell className="text-right">
                    {user.role !== "COORDENACAO" && <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>}
                    {isSuperadmin && u.id !== user.id && <Button variant="ghost" size="sm" onClick={() => setDelUser(u)} data-testid={`user-delete-${u.id}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Editar Usuário" : "Novo Usuário"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Nome *</Label><Input data-testid="user-name-input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label>E-mail *</Label><Input type="email" data-testid="user-email-input" value={form.email || ""} onChange={(e) => set("email", e.target.value)} disabled={!!editing} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Perfil</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ATIVO">ATIVO</SelectItem><SelectItem value="INATIVO">INATIVO</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {isSuperadmin && form.role !== "SUPERADMIN" && (
              <div><Label>Escola</Label>
                <Select value={form.school_id || ""} onValueChange={(v) => set("school_id", v)}>
                  <SelectTrigger data-testid="user-school-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.role === "PROFESSOR" && (
              <div><Label>Professor vinculado (cadastro de Professores)</Label>
                <Select value={form.teacher_id || ""} onValueChange={(v) => set("teacher_id", v)}>
                  <SelectTrigger data-testid="user-teacher-select"><SelectValue placeholder={form.school_id || user.school_id ? "Selecione o professor" : "Selecione a escola primeiro"} /></SelectTrigger>
                  <SelectContent>{teachers.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`user-teacher-option-${t.id}`}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-1 text-xs text-slate-500">Necessário para o professor lançar notas, faltas e diário das disciplinas que leciona.</p>
              </div>
            )}
            <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>{editing ? "Nova senha (opcional)" : "Senha"}</Label><Input type="password" data-testid="user-password-input" value={form.password || ""} onChange={(e) => set("password", e.target.value)} placeholder={editing ? "Deixe vazio para manter" : "Padrão: Acompanha@2026"} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.email} data-testid="user-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="user-delete-dialog">
          <DialogHeader><DialogTitle>Excluir usuário</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Tem certeza que deseja excluir <strong className="text-slate-800">{delUser?.name}</strong> ({delUser?.email})? Esta ação é permanente e não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelUser(null)}>Cancelar</Button>
            <Button onClick={doDelete} disabled={deleting} data-testid="user-delete-confirm-button" className="bg-rose-600 hover:bg-rose-700">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
